import { useCallback, useEffect, useRef, useState } from 'react'
import { updateBuilderDraft } from './api'

export const AUTOSAVE_DEBOUNCE_MS = 1500
const RETRY_DELAYS_MS = [1000, 2000, 4000]
const EMERGENCY_SNAPSHOT_VERSION = 1

export function emergencyStorageKey(vendorToken, draftPublicId) {
  return `gspress:builder-draft:${vendorToken}:${draftPublicId}`
}

function localStorageAvailable() {
  if (typeof window === 'undefined') return false

  try {
    return Boolean(window.localStorage)
  } catch {
    return false
  }
}

function validEmergencySnapshot(value, vendorToken, draftPublicId) {
  return Boolean(
    value &&
    value.version === EMERGENCY_SNAPSHOT_VERSION &&
    value.vendor_token === vendorToken &&
    value.draft_public_id === draftPublicId &&
    Number.isInteger(value.server_lock_version) &&
    value.server_lock_version >= 0 &&
    Number.isInteger(value.client_revision) &&
    value.client_revision > 0 &&
    value.layout_json &&
    typeof value.layout_json === 'object' &&
    !Array.isArray(value.layout_json) &&
    Array.isArray(value.image_configs) &&
    Array.isArray(value.uploaded_image_ids) &&
    value.vendor_sheet_size_id !== null &&
    value.vendor_sheet_size_id !== undefined &&
    Number.isInteger(Number(value.vendor_sheet_size_id)) &&
    Number(value.quantity) > 0
  )
}

export function readEmergencySnapshot(vendorToken, draftPublicId) {
  if (!localStorageAvailable() || !vendorToken || !draftPublicId) return null

  try {
    const parsed = JSON.parse(window.localStorage.getItem(emergencyStorageKey(vendorToken, draftPublicId)))
    return validEmergencySnapshot(parsed, vendorToken, draftPublicId) ? parsed : null
  } catch {
    return null
  }
}

export function removeEmergencySnapshot(vendorToken, draftPublicId) {
  if (!localStorageAvailable() || !vendorToken || !draftPublicId) return false

  try {
    window.localStorage.removeItem(emergencyStorageKey(vendorToken, draftPublicId))
    return true
  } catch {
    return false
  }
}

export function clearAcknowledgedEmergencySnapshot(vendorToken, draftPublicId, acknowledgedRevision) {
  const local = readEmergencySnapshot(vendorToken, draftPublicId)
  if (!local || local.client_revision > acknowledgedRevision) return false

  return removeEmergencySnapshot(vendorToken, draftPublicId)
}

function payloadFromEmergencySnapshot(snapshot) {
  return {
    vendor_sheet_size_id: snapshot.vendor_sheet_size_id,
    layout_json: snapshot.layout_json,
    image_configs: snapshot.image_configs,
    uploaded_image_ids: snapshot.uploaded_image_ids,
    quantity: snapshot.quantity,
    pricing_settings: snapshot.pricing_settings || null,
  }
}

function serverPayload(snapshot) {
  return {
    vendor_sheet_size_id: snapshot.payload.vendor_sheet_size_id,
    layout_json: snapshot.payload.layout_json,
    image_configs: snapshot.payload.image_configs,
    quantity: snapshot.payload.quantity,
    lock_version: snapshot.baseLockVersion,
    client_revision: snapshot.revision,
  }
}

function onlineNow() {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

export default function useBuilderAutosave({
  vendorToken,
  builderSessionId,
  draft,
  onDraftMetadataChange,
  onGeometryConflict,
}) {
  const [status, setStatus] = useState('idle')
  const [lastSavedAt, setLastSavedAt] = useState(draft?.last_autosaved_at || null)
  const draftRef = useRef(draft)
  const activeDraftPublicIdRef = useRef(draft?.public_id || null)
  const latestSnapshotRef = useRef(null)
  const latestClientRevisionRef = useRef(draft?.client_revision || 0)
  const lastAcknowledgedRevisionRef = useRef(draft?.client_revision || 0)
  const requestInFlightRef = useRef(false)
  const inFlightPromiseRef = useRef(null)
  const drainAfterFlightRef = useRef(false)
  const debounceTimerRef = useRef(null)
  const retryTimerRef = useRef(null)
  const retryAttemptRef = useRef(0)
  const conflictRef = useRef(false)
  const resolvingConflictRef = useRef(false)
  const onGeometryConflictRef = useRef(onGeometryConflict)
  onGeometryConflictRef.current = onGeometryConflict
  const failedRef = useRef(false)
  const mountedRef = useRef(true)

  const safelySetStatus = useCallback((nextStatus) => {
    if (mountedRef.current) setStatus(nextStatus)
  }, [])

  const clearTimer = useCallback((timerRef) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const persistEmergencySnapshot = useCallback((snapshot = latestSnapshotRef.current) => {
    const currentDraft = draftRef.current
    if (!localStorageAvailable() || !snapshot || !currentDraft?.public_id) return null

    const record = {
      version: EMERGENCY_SNAPSHOT_VERSION,
      vendor_token: vendorToken,
      draft_public_id: currentDraft.public_id,
      server_lock_version: snapshot.baseLockVersion,
      client_revision: snapshot.revision,
      layout_json: snapshot.payload.layout_json || {},
      image_configs: snapshot.payload.image_configs || [],
      uploaded_image_ids: snapshot.payload.uploaded_image_ids || [],
      vendor_sheet_size_id: snapshot.payload.vendor_sheet_size_id,
      quantity: snapshot.payload.quantity || 1,
      pricing_settings: snapshot.payload.pricing_settings || null,
      saved_locally_at: new Date().toISOString(),
    }

    try {
      window.localStorage.setItem(
        emergencyStorageKey(vendorToken, currentDraft.public_id),
        JSON.stringify(record)
      )
      return record
    } catch {
      return null
    }
  }, [vendorToken])

  useEffect(() => {
    draftRef.current = draft
    if (!draft?.public_id) return

    if (activeDraftPublicIdRef.current !== draft.public_id) {
      activeDraftPublicIdRef.current = draft.public_id
      latestSnapshotRef.current = null
      latestClientRevisionRef.current = draft.client_revision || 0
      lastAcknowledgedRevisionRef.current = draft.client_revision || 0
      retryAttemptRef.current = 0
      conflictRef.current = false
      failedRef.current = false
      safelySetStatus('idle')
    } else {
      lastAcknowledgedRevisionRef.current = Math.max(
        lastAcknowledgedRevisionRef.current,
        draft.client_revision || 0
      )
      latestClientRevisionRef.current = Math.max(
        latestClientRevisionRef.current,
        draft.client_revision || 0
      )
    }
    if (draft.last_autosaved_at && mountedRef.current) setLastSavedAt(draft.last_autosaved_at)
  }, [draft, safelySetStatus])

  const drain = useCallback(({ reconnecting = false, keepalive = false } = {}) => {
    if (conflictRef.current || !latestSnapshotRef.current || !draftRef.current) {
      return inFlightPromiseRef.current || Promise.resolve()
    }
    if (!onlineNow()) {
      safelySetStatus('offline')
      return Promise.resolve()
    }
    if (requestInFlightRef.current) {
      drainAfterFlightRef.current = true
      return inFlightPromiseRef.current
    }

    const snapshot = latestSnapshotRef.current
    const requestGeometryEpoch = Number(draftRef.current.geometry_epoch ?? 0)
    if (snapshot.revision <= lastAcknowledgedRevisionRef.current) return Promise.resolve()

    clearTimer(debounceTimerRef)
    clearTimer(retryTimerRef)
    requestInFlightRef.current = true
    safelySetStatus(reconnecting ? 'reconnected' : 'saving')

    const request = updateBuilderDraft(
      vendorToken,
      builderSessionId,
      draftRef.current.public_id,
      serverPayload(snapshot),
      ...(keepalive ? [{ keepalive: true }] : [])
    ).then((response) => {
      const acknowledgedRevision = Number(response.client_revision ?? snapshot.revision)
      lastAcknowledgedRevisionRef.current = Math.max(
        lastAcknowledgedRevisionRef.current,
        acknowledgedRevision
      )
      retryAttemptRef.current = 0
      failedRef.current = false
      draftRef.current = {
        ...draftRef.current,
        lock_version: response.lock_version,
        client_revision: acknowledgedRevision,
        last_autosaved_at: response.last_autosaved_at,
        status: response.status,
      }

      if (mountedRef.current) {
        setLastSavedAt(response.last_autosaved_at)
        onDraftMetadataChange?.(draftRef.current)
      }

      const latest = latestSnapshotRef.current
      if (!latest || latest.revision <= acknowledgedRevision) {
        latestSnapshotRef.current = null
        clearAcknowledgedEmergencySnapshot(
          vendorToken,
          draftRef.current.public_id,
          acknowledgedRevision
        )
        safelySetStatus('saved')
      } else {
        latest.baseLockVersion = response.lock_version
        persistEmergencySnapshot(latest)
        safelySetStatus('dirty')
        drainAfterFlightRef.current = true
      }
    }).catch((error) => {
      if (error.status === 409 && error.code === 'stale_draft') {
        conflictRef.current = true
        clearTimer(retryTimerRef)
        safelySetStatus('conflict')
        // Only a newer geometry epoch proves the server retargeted artwork.
        // An ordinary concurrent save must preserve local work, not enter the
        // adoption path that discards it. Never silently overwrite either side.
        const serverGeometryEpoch = Number(error.data?.geometry_epoch)
        if (!resolvingConflictRef.current && Number.isInteger(serverGeometryEpoch) && serverGeometryEpoch > requestGeometryEpoch) {
          onGeometryConflictRef.current?.(error.data)
        }
        return
      }

      failedRef.current = false
      if (!onlineNow()) {
        clearTimer(retryTimerRef)
        safelySetStatus('offline')
        return
      }

      const retryDelay = RETRY_DELAYS_MS[retryAttemptRef.current]
      if (retryDelay === undefined) {
        failedRef.current = true
        safelySetStatus('failed')
        return
      }

      retryAttemptRef.current += 1
      safelySetStatus('retrying')
      retryTimerRef.current = setTimeout(() => drain(), retryDelay)
    }).finally(() => {
      requestInFlightRef.current = false
      inFlightPromiseRef.current = null

      const hasNewerSnapshot = latestSnapshotRef.current &&
        latestSnapshotRef.current.revision > lastAcknowledgedRevisionRef.current
      if (
        mountedRef.current &&
        onlineNow() &&
        !conflictRef.current &&
        !failedRef.current &&
        !retryTimerRef.current &&
        (drainAfterFlightRef.current || hasNewerSnapshot)
      ) {
        drainAfterFlightRef.current = false
        drain()
      }
    })

    inFlightPromiseRef.current = request
    return request
  }, [
    builderSessionId,
    clearTimer,
    onDraftMetadataChange,
    persistEmergencySnapshot,
    safelySetStatus,
    vendorToken,
  ])

  const queueSave = useCallback((payload) => {
    if (!draftRef.current) return null

    const revision = ++latestClientRevisionRef.current
    const snapshot = {
      revision,
      baseLockVersion: draftRef.current.lock_version,
      payload,
    }
    latestSnapshotRef.current = snapshot
    retryAttemptRef.current = 0
    failedRef.current = false
    persistEmergencySnapshot(snapshot)
    clearTimer(debounceTimerRef)
    clearTimer(retryTimerRef)

    // A conflict stops server writes, not the local safety copy. The editor
    // remains usable, so retain subsequent edits while the conflict is resolved.
    if (conflictRef.current) return revision

    if (!onlineNow()) {
      safelySetStatus('offline')
      return revision
    }

    safelySetStatus('dirty')
    debounceTimerRef.current = setTimeout(() => drain(), AUTOSAVE_DEBOUNCE_MS)
    return revision
  }, [clearTimer, drain, persistEmergencySnapshot, safelySetStatus])

  const recoverSnapshot = useCallback((localSnapshot) => {
    if (!draftRef.current || !validEmergencySnapshot(
      localSnapshot,
      vendorToken,
      draftRef.current.public_id
    )) return false
    if (localSnapshot.client_revision <= lastAcknowledgedRevisionRef.current) {
      clearAcknowledgedEmergencySnapshot(
        vendorToken,
        draftRef.current.public_id,
        lastAcknowledgedRevisionRef.current
      )
      return false
    }

    latestClientRevisionRef.current = Math.max(
      latestClientRevisionRef.current,
      localSnapshot.client_revision
    )
    latestSnapshotRef.current = {
      revision: localSnapshot.client_revision,
      baseLockVersion: localSnapshot.server_lock_version,
      payload: payloadFromEmergencySnapshot(localSnapshot),
    }
    retryAttemptRef.current = 0
    failedRef.current = false

    if (!onlineNow()) {
      safelySetStatus('offline')
      return true
    }

    safelySetStatus('reconnected')
    drain({ reconnecting: true })
    return true
  }, [drain, safelySetStatus, vendorToken])

  const flush = useCallback(async (payload) => {
    if (payload) queueSave(payload)
    clearTimer(debounceTimerRef)
    clearTimer(retryTimerRef)
    if (!onlineNow()) {
      safelySetStatus('offline')
      return false
    }

    while (
      !conflictRef.current &&
      !failedRef.current &&
      latestSnapshotRef.current &&
      latestSnapshotRef.current.revision > lastAcknowledgedRevisionRef.current
    ) {
      if (requestInFlightRef.current) await inFlightPromiseRef.current
      else await drain()
      if (retryTimerRef.current) clearTimer(retryTimerRef)
    }
    return !conflictRef.current && !failedRef.current &&
      (!latestSnapshotRef.current || latestSnapshotRef.current.revision <= lastAcknowledgedRevisionRef.current)
  }, [clearTimer, drain, queueSave, safelySetStatus])

  const rebase = useCallback(async (serverDraft, { keepLocalVersion = false } = {}) => {
    if (!serverDraft?.public_id || serverDraft.public_id !== draftRef.current?.public_id) return false

    if (requestInFlightRef.current) {
      try {
        await inFlightPromiseRef.current
      } catch {
        // The fresh server metadata below supersedes an obsolete in-flight request.
      }
    }

    // Only explicit conflict recovery may replace another tab's revision.
    // Never replay coordinates against artwork that has since been retargeted.
    if (keepLocalVersion && (
      !conflictRef.current || !latestSnapshotRef.current ||
      serverDraft.status !== 'active' ||
      !Number.isInteger(serverDraft.lock_version) ||
      !Number.isInteger(serverDraft.client_revision) ||
      Number(serverDraft.geometry_epoch ?? 0) !== Number(draftRef.current.geometry_epoch ?? 0)
    )) return false

    clearTimer(debounceTimerRef)
    clearTimer(retryTimerRef)
    conflictRef.current = false
    failedRef.current = false
    retryAttemptRef.current = 0

    const serverRevision = Number(serverDraft.client_revision || 0)
    lastAcknowledgedRevisionRef.current = serverRevision
    latestClientRevisionRef.current = Math.max(latestClientRevisionRef.current, serverRevision)
    draftRef.current = {
      ...draftRef.current,
      lock_version: serverDraft.lock_version,
      client_revision: serverRevision,
      last_autosaved_at: serverDraft.last_autosaved_at,
      status: serverDraft.status,
    }

    const latest = latestSnapshotRef.current
    if (keepLocalVersion && latest) {
      // Revisions are per browser, not proof that another tab saved our work.
      latest.revision = ++latestClientRevisionRef.current
      latest.baseLockVersion = serverDraft.lock_version
      persistEmergencySnapshot(latest)
    } else if (latest && latest.revision <= serverRevision) {
      latestSnapshotRef.current = null
      clearAcknowledgedEmergencySnapshot(vendorToken, serverDraft.public_id, serverRevision)
    } else if (latest) {
      latest.baseLockVersion = serverDraft.lock_version
      persistEmergencySnapshot(latest)
    }

    if (mountedRef.current) {
      setLastSavedAt(serverDraft.last_autosaved_at)
      onDraftMetadataChange?.(draftRef.current)
    }

    if (!latestSnapshotRef.current) {
      safelySetStatus('saved')
      return true
    }

    safelySetStatus('reconnected')
    resolvingConflictRef.current = keepLocalVersion
    try {
      await drain({ reconnecting: true })
    } finally {
      resolvingConflictRef.current = false
    }
    if (keepLocalVersion && latestSnapshotRef.current) {
      // A failed or competing write needs another explicit choice. Keep the
      // safety copy and do not retry an overwrite in the background.
      conflictRef.current = true
      clearTimer(retryTimerRef)
      safelySetStatus('conflict')
      return false
    }
    return !conflictRef.current && !failedRef.current
  }, [
    clearTimer,
    drain,
    onDraftMetadataChange,
    persistEmergencySnapshot,
    safelySetStatus,
    vendorToken,
  ])

  // Adoption's counterpart to rebase. The server retargeted this draft's
  // geometry, so pending local work describes artwork that no longer exists at
  // those coordinates. rebase would re-stamp that whole-layout snapshot with the
  // server's new lock_version and resend it, overwriting the retarget — and the
  // submission handshake would then certify the result, because the versions
  // match. So this drops the snapshot instead of replaying it.
  const discard = useCallback(async (serverDraft) => {
    if (!serverDraft?.public_id || serverDraft.public_id !== draftRef.current?.public_id) return false

    if (requestInFlightRef.current) {
      try {
        await inFlightPromiseRef.current
      } catch {
        // An obsolete in-flight request cannot matter — its work is discarded too.
      }
    }

    clearTimer(debounceTimerRef)
    clearTimer(retryTimerRef)
    conflictRef.current = false
    failedRef.current = false
    retryAttemptRef.current = 0
    drainAfterFlightRef.current = false

    const serverRevision = Number(serverDraft.client_revision || 0)
    lastAcknowledgedRevisionRef.current = serverRevision
    latestClientRevisionRef.current = serverRevision
    draftRef.current = {
      ...draftRef.current,
      lock_version: serverDraft.lock_version,
      client_revision: serverRevision,
      last_autosaved_at: serverDraft.last_autosaved_at,
      status: serverDraft.status,
    }

    latestSnapshotRef.current = null
    removeEmergencySnapshot(vendorToken, serverDraft.public_id)

    if (mountedRef.current) {
      setLastSavedAt(serverDraft.last_autosaved_at)
      onDraftMetadataChange?.(draftRef.current)
    }

    safelySetStatus('saved')
    return true
  }, [clearTimer, onDraftMetadataChange, safelySetStatus, vendorToken])

  useEffect(() => {
    const handleOffline = () => {
      clearTimer(debounceTimerRef)
      clearTimer(retryTimerRef)
      if (latestSnapshotRef.current || requestInFlightRef.current) safelySetStatus('offline')
    }
    const handleOnline = () => {
      clearTimer(debounceTimerRef)
      clearTimer(retryTimerRef)
      retryAttemptRef.current = 0
      failedRef.current = false
      if (!latestSnapshotRef.current ||
          latestSnapshotRef.current.revision <= lastAcknowledgedRevisionRef.current) return

      safelySetStatus('reconnected')
      drainAfterFlightRef.current = requestInFlightRef.current
      drain({ reconnecting: true })
    }
    const handlePageHide = () => {
      const snapshot = latestSnapshotRef.current
      const currentDraft = draftRef.current
      if (!snapshot || !currentDraft) return

      persistEmergencySnapshot(snapshot)
      // Use the same single-flight queue and acknowledgement path as normal
      // saves. A separate fire-and-forget write races the debounce timer when
      // the browser restores the page and can manufacture a stale conflict.
      drain({ keepalive: true })
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [
    builderSessionId,
    clearTimer,
    drain,
    persistEmergencySnapshot,
    safelySetStatus,
    vendorToken,
  ])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearTimer(debounceTimerRef)
      clearTimer(retryTimerRef)
    }
  }, [clearTimer])

  return {
    status,
    lastSavedAt,
    queueSave,
    recoverSnapshot,
    flush,
    rebase,
    discard,
  }
}
